import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { push } from 'connected-react-router';
import { Icon } from 'antd';
import decode from 'jwt-decode';
import { ADMIN, USER_HOST, SUPER_HOST } from '../constants';
import { getPrimaryRole } from '../../../utils/rolePriority';
import './AdminDashboardLayout.scss';

const USER_GUEST = 'role.user';

const AdminDashboardLayout = ({ children }) => {
  const dispatch = useDispatch();
  const { country } = useParams();
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const locationPath = useSelector((state) => state?.router?.location?.pathname || '');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const userType = authDetails ? getPrimaryRole(decode(authDetails)?.authorities) : null;

  const basePath = `/${country}/dashboard`;

  const handleNavigation = (path) => {
    dispatch(push(path));
  };

  const isActive = (path, key) => {
    if (key === 'myspaces') {
      return locationPath.includes(path) || locationPath.includes(`${basePath}/calendar`) || locationPath.includes(`${basePath}/visits`);
    }
    return locationPath.includes(path);
  };

  const adminNavItems = [
    { label: 'Dashboard', path: `${basePath}/admin`, key: 'admin', icon: 'dashboard' },
    { label: 'Reservations', path: `${basePath}/reservations`, key: 'reservations', icon: 'calendar' },
    { label: 'Spaces', path: `${basePath}/myspaces`, key: 'myspaces', icon: 'shop' },
    { label: 'My Bookings', path: `${basePath}/mybooking`, key: 'mybooking', icon: 'shopping-cart' },
    { label: 'User Management', path: `${basePath}/users`, key: 'users', disabled: true, icon: 'team' },
    { label: 'Reports', path: `${basePath}/reports`, key: 'reports', icon: 'bar-chart' },
    { label: 'List a space', path: `${basePath}/list-a-space`, key: 'list-space', icon: 'plus' },
  ];

  const hostNavItems = [
    { label: 'My Spaces', path: `${basePath}/myspaces`, key: 'myspaces', icon: 'home' },
    { label: 'Reservations', path: `${basePath}/reservations`, key: 'reservations', icon: 'calendar' },
    { label: 'My Bookings', path: `${basePath}/mybooking`, key: 'mybooking', icon: 'shopping-cart' },
    { label: 'Reports', path: `${basePath}/host-reports`, key: 'reports', icon: 'bar-chart' },
    { label: 'Promotions', path: `${basePath}/promotions`, key: 'promotions', icon: 'rocket' },
    { label: 'List a space', path: `${basePath}/list-a-space`, key: 'list-space', icon: 'plus' },
  ];

  const superHostNavItems = [
    { label: 'Managed Spaces', path: `${basePath}/myspaces`, key: 'myspaces', icon: 'home' },
    { label: 'Reservations', path: `${basePath}/reservations`, key: 'reservations', icon: 'calendar' },
    { label: 'My Bookings', path: `${basePath}/mybooking`, key: 'mybooking', icon: 'shopping-cart' },
    { label: 'Host Management', path: `${basePath}/user-management`, key: 'host-management', icon: 'team' },
    { label: 'Reports', path: `${basePath}/host-reports`, key: 'reports', icon: 'bar-chart' },
    { label: 'Promotions', path: `${basePath}/promotions`, key: 'promotions', icon: 'rocket' },
    { label: 'List a space', path: `${basePath}/list-a-space`, key: 'list-space', icon: 'plus' },
  ];

  const guestNavItems = [
    { label: 'My Bookings', path: `${basePath}/mybooking`, key: 'mybooking', icon: 'shopping-cart' },
  ];

  let navigationItems = [];
  if (userType === ADMIN) {
    navigationItems = adminNavItems;
  } else if (userType === SUPER_HOST) {
    navigationItems = superHostNavItems;
  } else if (userType === USER_HOST) {
    navigationItems = hostNavItems;
  } else {
    navigationItems = guestNavItems;
  }

  const isHost = userType === USER_HOST;

  return (
    <div className="admin-dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${isActive(item.path, item.key) ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                onClick={() => !item.disabled && handleNavigation(item.path)}
                disabled={item.disabled}
              >
                <Icon type={item.icon} className="nav-icon" />
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>

        </div>
      </aside>

      <nav className="mobile-tabs">
        <div className="mobile-tabs-container">
          {navigationItems.map((item) => (
            <button
              key={item.key}
              className={`mobile-tab-item ${isActive(item.path, item.key) ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
              onClick={() => !item.disabled && handleNavigation(item.path)}
              disabled={item.disabled}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="admin-main-content">
        {children}
      </main>
    </div>
  );
};

export default AdminDashboardLayout;
